import React, { useState, useCallback, useMemo } from "react";
import * as XLSX from "xlsx";
import { RouteComponentProps } from "@reach/router";
import { Button, ButtonType, Spinner } from "../Components";
import { awaitJSON, MetaLambdaFetch } from "../utils/fetch-utils";
import { AdAccountInfo, AdSetRow } from "@blisspointmedia/bpm-types/dist/MetaBuying";
import { PageTab } from "./MetaBuying";
import { useSetError } from "../redux/modals";
import * as R from "ramda";
import { IMAGE, VIDEO, CAMPAIGN, AD_SET, AD } from "./MetaBuyingConstants";
import { validateName } from "./MetaBuyingUtils";

const TAB_OPTIONS = ["Upload File"];

interface MetaBuyingUploadProps {
  selectedAdAccount: AdAccountInfo;
  adVideoThumbnail: string;
  refreshData: (tab: PageTab) => void;
  adSets: AdSetRow[];
  namingConventions: {
    [CAMPAIGN]: number[];
    [AD_SET]: number[];
    [AD]: number[];
  };
  client: string;
  dimensions: Record<string, any>;
}

const MetaBuyingUpload: React.FC<MetaBuyingUploadProps & RouteComponentProps> = ({
  selectedAdAccount,
  refreshData,
  adSets,
  namingConventions,
  client,
  dimensions,
  adVideoThumbnail,
}): JSX.Element => {
  const setError = useSetError();

  const { account_id, business_manager } = selectedAdAccount;

  const [file, setFile] = useState<File | undefined>();
  const [importing, setImporting] = useState<boolean>(false);

  const adSetIds = useMemo(() => adSets.map(({ id }) => id), [adSets]);

  const saveChanges = useCallback(
    async rows => {
      try {
        await MetaLambdaFetch("/saveMetaUpload", {
          method: "POST",
          body: { rows, adVideoThumbnail, accountId: account_id },
        });
      } catch (e) {
        console.error(e);
        setError({
          title: "Upload Error",
          message: e instanceof Error ? e.message : String(e),
        });
      }
    },
    [adVideoThumbnail, account_id, setError]
  );

  const importPlan = useCallback(() => {
    setImporting(true);

    const reader = new FileReader();
    reader.onload = async e => {
      try {
        const binaryString = e?.target?.result;

        const rows: {
          [key: string]: string;
          "Asset ID": string;
          "Facebook Page": string;
        }[] = convertFileToJson(binaryString as ArrayBuffer).map(row => ({
          ...row,
          "Asset ID": row["Asset ID"].toString(),
          "Facebook Page": row["Facebook Page"].toString(),
        }));

        const videosRes = await MetaLambdaFetch("/getMetaBuyingVideos", {
          params: { accountIDs: account_id },
        });
        const imagesRes = await MetaLambdaFetch("/getMetaBuyingImages", {
          params: { accountIDs: account_id },
        });
        const videoAssets = await awaitJSON(videosRes);
        const imageAssets = await awaitJSON(imagesRes);
        const videoIds = videoAssets[account_id].map(({ video_id }) => video_id);
        const imageIds = imageAssets[account_id].map(({ hash }) => hash);

        /**
         * Validate that the uploaded rows meet requirements.
         */
        rows.forEach(
          (
            {
              "Ad Set ID": adSetId,
              "Ad Name": adName,
              "Asset ID": assetId,
              "Creative Type": creativeType,
            },
            rowIndex
          ) => {
            if (
              !validateName(adName, AD, namingConventions, dimensions, client, business_manager)
            ) {
              throw new Error(
                `Row ${rowIndex}: ad name '${adName}' does not follow ${client}'s naming convention`
              );
            }

            if (!R.includes(adSetId, adSetIds)) {
              throw new Error(`Row ${rowIndex}: ad set with ID ${adSetId} does not exist`);
            }

            if (!R.includes(creativeType, [IMAGE, VIDEO])) {
              throw new Error(
                `Row ${rowIndex}: '${creativeType}' is not a valid creative type; a type of 'VIDEO' or 'IMAGE' is required`
              );
            } else if (creativeType === VIDEO && !R.includes(assetId.toString(), videoIds)) {
              throw new Error(`Row ${rowIndex}: video with ID ${assetId} does not exist`);
            } else if (creativeType === IMAGE && !R.includes(assetId.toString(), imageIds)) {
              throw new Error(`Row ${rowIndex}: image with ID ${assetId} does not exist`);
            }
          }
        );

        await saveChanges(rows);
      } catch (e) {
        setError({
          title: "Upload Error",
          message: e instanceof Error ? e.message : String(e),
        });
      }

      setImporting(false);
      refreshData(PageTab.DRAFTS);
    };
    reader.readAsArrayBuffer(file as Blob);
  }, [
    file,
    account_id,
    adSetIds,
    business_manager,
    client,
    dimensions,
    namingConventions,
    saveChanges,
    refreshData,
    setError,
  ]);

  return (
    <div className="metaBuyingWidgetWrapper">
      <div className="metaBuyingWidget">
        <h2>Upload</h2>
        <div className="metaBuyingWidgetBody">
          <Button
            size="sm"
            className="metaBuyingButton upload"
            type={ButtonType.EMPTY}
            onClick={() => downloadTemplate()}
          >
            Download Template
          </Button>
          <div
            className="metaBuyingUploadPrompt"
            style={{
              border: "1px solid #CBD2E1",
              borderRadius: "4px",
              padding: "16px 24px 24px 24px",
            }}
          >
            <div className="metaBuyingTabs" style={{ paddingLeft: "0px" }}>
              {TAB_OPTIONS.map(tab => (
                <Button
                  type={ButtonType.EMPTY}
                  // className={selectedTab === tab ? "selected" : ""}
                  onClick={() => {}}
                  key={tab}
                >
                  {tab}
                </Button>
              ))}
            </div>

            {/* <Form.Group>
              <Form.Label>Link your file below and grant us view access.</Form.Label>
              <Form.Control value={fileLink} onChange={e => setFileLink(e.target.value)} />
            </Form.Group> */}

            <input
              className="metaUploadFileInput"
              type="file"
              id="file_input_id"
              onChange={e => {
                const uploadedFile = e.target.files;
                const file = uploadedFile?.[0];
                setFile(file);
              }}
              style={{ marginTop: "16px", paddingLeft: "16px" }}
            />
          </div>
        </div>

        <Button
          className="submitSelections"
          type={ButtonType.OUTLINED}
          onClick={importPlan}
          disabled={importing}
        >
          {importing ? <Spinner /> : "Save as Draft"}
        </Button>
      </div>
    </div>
  );
};

/**
 * Download template for the Meta Buying upload.
 */
const downloadTemplate = (): void => {
  const fileName = "meta-upload-template.xlsx";

  let workbook: XLSX.WorkBook = { SheetNames: [], Sheets: {} };
  let worksheet = XLSX.utils.json_to_sheet([
    {
      "Ad Set ID": "",
      "Ad Name": "",
      "Creative Type": "",
      "Primary Text": "",
      "URL Parameters": "",
      "Asset ID": "",
      Headline: "",
      Description: "",
      "Call to Action": "",
      "Website URL": "",
      "Facebook Page": "",
      // "Instagram Account": "",
    },
  ]);

  workbook.SheetNames.push(fileName);
  workbook.Sheets[fileName] = worksheet;

  XLSX.writeFile(workbook, fileName);
};

/**
 * Convert the uploaded Excel file to JSON.
 */
const convertFileToJson = (file: ArrayBuffer): Record<string, string>[] => {
  // Create a workbook using the imported file.
  const workbook = XLSX.read(file, { type: "buffer" });
  // Get first sheet of workbook
  const worksheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[worksheetName];

  // Convert worksheet to JSON
  const rows: Record<string, string>[] = XLSX.utils.sheet_to_json(worksheet);
  return rows;
};

export default MetaBuyingUpload;
